import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { type FaqItem, safeParseLinks } from "./useFaqsManager";
import { LinksConfigSection } from "./ProjectsSubSections";

interface FaqFormFieldsProps {
  editingFaq: Partial<FaqItem>;
  onChange: (key: keyof FaqItem, value: any) => void;
}

function FaqFormFields({ editingFaq, onChange }: FaqFormFieldsProps) {
  return (
    <div className="space-y-5">
      {/* Row 1: Questions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label htmlFor="question_en" className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
            Question (English) <span className="text-destructive">*</span>
          </Label>
          <Input
            id="question_en"
            placeholder="e.g. What is Uğurcan Uzunkaya's focus?"
            value={editingFaq.question_en || ""}
            onChange={(e) => onChange("question_en", e.target.value)}
            className="border-border/60 font-sans"
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="question_tr" className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
            Question (Turkish)
          </Label>
          <Input
            id="question_tr"
            placeholder="Örn. Uğurcan Uzunkaya'nın odak noktası nedir?"
            value={editingFaq.question_tr || ""}
            onChange={(e) => onChange("question_tr", e.target.value)}
            className="border-border/60 font-sans"
          />
        </div>
      </div>

      {/* Row 2: Answers */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label htmlFor="answer_en" className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
            Answer (English) <span className="text-destructive">*</span>
          </Label>
          <Textarea
            id="answer_en"
            placeholder="Enter detailed answer here..."
            rows={4}
            value={editingFaq.answer_en || ""}
            onChange={(e) => onChange("answer_en", e.target.value)}
            className="border-border/60 font-sans resize-none leading-relaxed"
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="answer_tr" className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
            Answer (Turkish)
          </Label>
          <Textarea
            id="answer_tr"
            placeholder="Buraya detaylı cevabı girin..."
            rows={4}
            value={editingFaq.answer_tr || ""}
            onChange={(e) => onChange("answer_tr", e.target.value)}
            className="border-border/60 font-sans resize-none leading-relaxed"
          />
        </div>
      </div>
    </div>
  );
}

interface FaqEditFormProps {
  editingFaq: Partial<FaqItem>;
  setEditingFaq: React.Dispatch<React.SetStateAction<Partial<FaqItem> | null>>;
  activeDraft: any;
  liveItem: FaqItem | undefined;
  onSave: () => void;
  onSaveDraft: () => void;
  onDiscardDraft: () => void;
  onCancel: () => void;
}

export function FaqEditForm({
  editingFaq,
  setEditingFaq,
  activeDraft,
  liveItem,
  onSave,
  onSaveDraft,
  onDiscardDraft,
  onCancel,
}: FaqEditFormProps) {
  const updateField = (key: keyof FaqItem, value: any) => {
    setEditingFaq((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  const isNew = !liveItem;
  const hasChanges = activeDraft || JSON.stringify(editingFaq) !== JSON.stringify(liveItem);

  return (
    <div className="bg-card border border-border/60 rounded-2xl p-6 shadow-sm space-y-6">
      <div className="flex items-center justify-between border-b border-border/40 pb-4">
        <div>
          <h4 className="text-lg font-bold text-foreground">
            {isNew ? "Create FAQ Item" : "Edit FAQ Item"}
          </h4>
          <p className="text-xs text-muted-foreground">
            Configure dynamic FAQ details in English and Turkish.
          </p>
        </div>
        {activeDraft && (
          <span className="text-[10px] font-extrabold uppercase px-2.5 py-1 bg-amber-500/10 text-amber-500 border border-amber-500/15 rounded-full">
            Staged Draft Active
          </span>
        )}
      </div>

      <FaqFormFields editingFaq={editingFaq} onChange={updateField} />

      <LinksConfigSection
        links={safeParseLinks(editingFaq.links)}
        onChange={(newLinks) => updateField("links", newLinks)}
      />

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-4 border-t border-border/40">
        <div className="flex gap-2">
          {!isNew && (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={onSaveDraft}
                disabled={!hasChanges}
                className="border-amber-500/40 text-amber-500 hover:bg-amber-500/10 font-semibold"
              >
                Save Draft
              </Button>
              {activeDraft && (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={onDiscardDraft}
                  className="text-destructive hover:bg-destructive/10 font-semibold"
                >
                  Discard Draft
                </Button>
              )}
            </>
          )}
        </div>

        <div className="flex gap-2 justify-end">
          <Button
            type="button"
            variant="ghost"
            onClick={onCancel}
            className="font-semibold"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={onSave}
            className="bg-brand-primary text-white hover:bg-brand-primary/95 font-semibold"
          >
            Publish Live
          </Button>
        </div>
      </div>
    </div>
  );
}

export default FaqEditForm;
